import { Alert, Button, Checkbox, Drawer, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasServiceLabelSelectionChanged, type ServiceLabelLine } from '@washday/utilities';
import type { IStore } from '../../interfaces/Store';
import {
  fetchServiceLabelSnapshot,
  type ServiceLabelSnapshot
} from '../../services/serviceLabelSnapshot';
import { tryPrintServiceLabels } from '../../utils/ticketHelper';

export default function ServiceLabelSelector({
  opened,
  onClose,
  orderId,
  store,
  logo = ''
}: {
  opened: boolean;
  onClose: () => void;
  orderId: string;
  store: IStore;
  logo?: string;
}) {
  const { t } = useTranslation(['Labels']);
  const [snapshot, setSnapshot] = useState<ServiceLabelSnapshot | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const requestGeneration = useRef(0);
  const storeId = store?._id || '';
  const activeScope = useRef({ opened, orderId, storeId });
  activeScope.current = { opened, orderId, storeId };
  const busy = loading || printing;
  const fetchFresh = () =>
    fetchServiceLabelSnapshot(orderId, storeId, {
      unassigned: t('Labels:unassigned'),
      unavailable: t('Labels:unavailable'),
      service: t('Labels:service')
    });

  useEffect(() => {
    requestGeneration.current += 1;
    setPrinting(false);
    if (!opened) return;
    let active = true;
    setLoading(true);
    setSnapshot(null);
    setError(null);
    setSelected([]);
    fetchServiceLabelSnapshot(orderId, storeId, {
      unassigned: t('Labels:unassigned'),
      unavailable: t('Labels:unavailable'),
      service: t('Labels:service')
    })
      .then((fresh) => {
        if (!active) return;
        setSnapshot(fresh);
        setSelected(fresh.lines.map((line) => line.key));
      })
      .catch(() => {
        if (active) setError(t('Labels:refreshError'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      requestGeneration.current += 1;
    };
  }, [opened, orderId, storeId, retry, t]);

  const confirm = async () => {
    if (busy || selected.length === 0 || !snapshot) return;
    setPrinting(true);
    setError(null);
    const generation = requestGeneration.current;
    const isCurrentRequest = () => generation === requestGeneration.current &&
      activeScope.current.opened && activeScope.current.orderId === orderId &&
      activeScope.current.storeId === storeId;
    try {
      const fresh = await fetchFresh();
      if (!isCurrentRequest()) return;
      const freshLines = fresh.lines.filter((line) => selected.includes(line.key));
      // Index-based legacy identities cannot survive a line replacement safely.
      const changed = hasServiceLabelSelectionChanged(snapshot.lines, fresh.lines, selected);
      setSnapshot(fresh);
      if (changed) {
        setSelected(freshLines.map((line) => line.key));
        setError(t('Labels:linesChanged'));
        return;
      }
      const freshStore = typeof fresh.order.createdIn === 'object' ? fresh.order.createdIn : store;
      if (!isCurrentRequest()) return;
      const result = await tryPrintServiceLabels(fresh.order, freshStore, t, freshLines, logo);
      if (!isCurrentRequest()) return;
      if (result.busy) return;
      if (result.failed.length > 0) {
        setSelected(result.failed.map(({ item }: { item: ServiceLabelLine }) => item.key));
      } else if (result.sent.length > 0 || (result.fallback && result.prepared?.length > 0)) {
        onClose();
      }
    } catch {
      // Refuse stale assignment data when either refresh fails.
      if (!isCurrentRequest()) return;
      setSnapshot(null);
      setSelected([]);
      setError(t('Labels:refreshError'));
    } finally {
      if (isCurrentRequest()) setPrinting(false);
    }
  };

  const lines = snapshot?.lines || [];
  const allSelected = lines.length > 0 && selected.length === lines.length;
  return (
    <Drawer
      opened={opened}
      onClose={() => {
        if (!busy) onClose();
      }}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      withCloseButton={!busy}
      title={t('Labels:selectorTitle')}
      position="right"
      size="md"
    >
      <Stack>
        <Text size="sm" c="dimmed">
          {t('Labels:selectorHelp')}
        </Text>
        {error && <Alert color="red">{error}</Alert>}
        {loading && <Loader aria-label={t('Labels:loading')} />}
        {!loading && !snapshot && (
          <Button onClick={() => setRetry((value) => value + 1)}>{t('Labels:retry')}</Button>
        )}
        {snapshot && lines.length === 0 && <Text>{t('Labels:empty')}</Text>}
        {lines.length > 0 && (
          <>
            <Checkbox
              label={t('Labels:selectAll')}
              checked={allSelected}
              indeterminate={selected.length > 0 && !allSelected}
              disabled={busy}
              onChange={(event) =>
                setSelected(event.currentTarget.checked ? lines.map((line) => line.key) : [])
              }
            />
            {lines.map((line) => (
              <Paper key={line.key} withBorder p="sm">
                <Group align="flex-start" wrap="nowrap">
                  <Checkbox
                    mt={3}
                    aria-label={line.name}
                    checked={selected.includes(line.key)}
                    disabled={busy}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      setSelected((current) =>
                        checked ? [...current, line.key] : current.filter((key) => key !== line.key)
                      );
                    }}
                  />
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Text fw={600} style={{ overflowWrap: 'anywhere' }}>
                      {line.name}
                    </Text>
                    <Text size="sm">
                      {t('Labels:quantity')}: {line.quantity}
                    </Text>
                    <Text
                      size="sm"
                      c={line.employeeState === 'unavailable' ? 'orange' : undefined}
                      style={{ overflowWrap: 'anywhere' }}
                    >
                      {line.employeeName}
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            ))}
            <Button onClick={confirm} loading={printing} disabled={busy || selected.length === 0}>
              {t('Labels:printSelected', { count: selected.length })}
            </Button>
          </>
        )}
      </Stack>
    </Drawer>
  );
}
